// src/features/stock-info/components/ValuationRatiosTable.tsx
//
// Component Island: Valuation Ratios. Fetches aggregated TTM valuation
// multiples from GET /stock-info/{symbol}/valuation-ratios (backed by FMP
// /stable/ratios-ttm + /stable/profile + /stable/analyst-estimates) and
// renders a professionally formatted key/value table:
//   P/E, Forward P/E (current & next FY), P/BV, P/Sales, P/FCF,
//   Dividend Yield, Beta, PEG Ratio.
//
// Each island fetches independently so a slow/failing endpoint never blocks
// the rest of the page.

import { useEffect, useState } from 'react';

import { fetchValuationRatios, type ValuationRatios } from '../api';

const formatMultiple = (val?: number) =>
    val != null ? `${val.toFixed(2)}x` : 'N/A';

const formatRatio = (val?: number) =>
    val != null ? val.toFixed(2) : 'N/A';

// dividend_yield arrives as a FRACTION (e.g. 0.0033) → render as a percentage.
const formatYield = (val?: number) =>
    val != null ? `${(val * 100).toFixed(2)}%` : 'N/A';

export const ValuationRatiosTable = ({ symbol }: { symbol: string }) => {
    const [data, setData] = useState<ValuationRatios | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchValuationRatios(symbol)
            .then((res) => {
                if (!cancelled) setData(res ?? null);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Valuation ratios unavailable.');
                    setData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const rows: { label: string; value: string; hint?: string }[] = data
        ? [
              { label: 'P/E Ratio', value: formatMultiple(data.pe_ratio), hint: 'TTM' },
              {
                  label: `Forward P/E (${data.forward_pe_current_year ?? 'FY1'})`,
                  value: formatMultiple(data.forward_pe_current),
                  hint: 'Est.',
              },
              {
                  label: `Forward P/E (${data.forward_pe_next_year ?? 'FY2'})`,
                  value: formatMultiple(data.forward_pe_next),
                  hint: 'Est.',
              },
              { label: 'P/BV Ratio', value: formatMultiple(data.pb_ratio), hint: 'TTM' },
              { label: 'P/Sales Ratio', value: formatMultiple(data.ps_ratio), hint: 'TTM' },
              { label: 'P/FCF Ratio', value: formatMultiple(data.pfcf_ratio), hint: 'TTM' },
              { label: 'Dividend Yield', value: formatYield(data.dividend_yield), hint: 'TTM' },
              { label: 'Beta', value: formatRatio(data.beta), hint: '5Y' },
              { label: 'PEG Ratio', value: formatRatio(data.peg_ratio), hint: 'TTM' },
          ]
        : [];

    return (
        <div className="surface-card border-t-4 border-violet-500 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h2 className="section-title mb-0">Valuation Ratios</h2>
                <span className="px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 text-xs font-bold uppercase tracking-wider">
                    Trailing 12M
                </span>
            </div>

            <div className="relative min-h-[300px] flex-1">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-violet-500 font-medium animate-pulse">
                            Loading Ratios…
                        </span>
                    </div>
                )}

                {!isLoading && (error || !data) && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error || 'No valuation data available.'}
                    </div>
                )}

                {!isLoading && !error && data && (
                    <table className="w-full text-sm">
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr
                                    key={row.label}
                                    className={`border-b border-slate-100 dark:border-slate-700/60 ${
                                        idx % 2 === 1
                                            ? 'bg-slate-50/60 dark:bg-slate-800/30'
                                            : ''
                                    }`}
                                >
                                    <td className="py-2.5 px-3 text-slate-600 dark:text-slate-300 font-medium flex items-center gap-2">
                                        {row.label}
                                        {row.hint && (
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                                                {row.hint}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 dark:text-white tabular-nums">
                                        {row.value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ValuationRatiosTable;
